import { Alert, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Project } from '../../Projects/projects';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from 'react-redux';
import { projectSlice } from '../../../redux/ProjectSlice';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { ApplicationActions } from '../../../utils/constants';
import { useDeleteProject } from '../../../api/queries';
import { useState } from 'react';
import { isBrowserOffline } from '../../../utils/networkStatus';

interface RemoveShareCodeDialogProps {
  project: Project | null;
  onClose: () => void;
  open: boolean;
}

const RemoveShareCodeDialog = ({ onClose, project }: RemoveShareCodeDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const deleteProjectMutation = useDeleteProject();

  const handleCancelRemoveShareCode = () => {
    onClose();
  };

  const handleRemoveShareCode = async () => {
    if (!project) return;

    const shareCode = project.shareCode;
    const pin = project.pin;

    if (!shareCode || !pin) {
      setErrorMessage(
        t(
          'removeShareCodeDialog.error.missing-credentials',
          'This project is missing share code or collaborator PIN information. Unable to remove from the platform.'
        )
      );
      return;
    }

    if (isBrowserOffline()) {
      setErrorMessage(t('removeShareCodeDialog.error.offline', 'You appear to be offline. Connect to the internet to remove a share code.'));
      return;
    }

    setErrorMessage('');

    try {
      await deleteProjectMutation.mutateAsync({ shareCode, pin });

      dispatch(projectSlice.actions.onClearProjectShareCode({ projectId: project.id }));

      onClose();
      dispatch(
        applicationSlice.actions.onOpenDialog({
          ...ApplicationActions.Dialogs.REMOVE_COMPLETE_SHARE_CODE,
          options: { ...ApplicationActions.Dialogs.REMOVE_COMPLETE_SHARE_CODE.options, project: project },
        })
      );
    } catch (error) {
      console.error('Failed to remove share code:', error);
      setErrorMessage(t('removeShareCodeDialog.error.general', 'Failed to remove share code. Please check your connection and try again.'));
    }
  };

  const projectName = project?.name;

  return (
    <>
      <DialogTitle variant="dialogTitle" align="center">
        <Trans i18nKey={'removeShareCodeDialog.title'}>Remove Share Code for {{ projectName }}</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 5.5, py: 3 }}>
        <Stack direction="column" spacing={2}>
          <Typography>
            <Trans i18nKey={'removeShareCodeDialog.main.explain'}>
              This will remove the project from the sphero Central Platform and remove the Share Code and Edit Pin, making it inaccessible from the
              direct link or code.
            </Trans>
          </Typography>
          <Typography>
            <Trans i18nKey={'removeShareCodeDialog.main.remove'}>Are you sure you want to remove?</Trans>
          </Typography>
          {(errorMessage || deleteProjectMutation.isError) && (
            <Alert severity="error">
              {errorMessage || t('removeShareCodeDialog.error.general', 'Failed to remove share code. Please check your connection and try again.')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'center', px: 2, py: 1 }}>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <Button onClick={handleCancelRemoveShareCode} disabled={deleteProjectMutation.isPending}>
            <Trans i18nKey="removeShareCodeDialog.button.cancel">Cancel</Trans>
          </Button>

          <Button
            onClick={() => void handleRemoveShareCode()}
            disabled={deleteProjectMutation.isPending}
            startIcon={deleteProjectMutation.isPending ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteProjectMutation.isPending ? (
              <Trans i18nKey="removeShareCodeDialog.button.removing">Removing...</Trans>
            ) : (
              <Trans i18nKey="removeShareCodeDialog.button.remove-share-code">Remove Share Code</Trans>
            )}
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
};

export default RemoveShareCodeDialog;
